import { useEffect, useState } from 'react';
import {
  getInventoryAddPageContext,
  verifyInventoryAddLink
} from '../Api/Inventory.api';
import { MOCK_BRANCH, MOCK_RESTAURANT_NAME, MOCK_SUPPLIER } from '../mockData';
import type { InventorySupplier, RestaurantBranch } from '../Types';

interface InventoryAddSession {
  sessionToken: string | null;
  branch: RestaurantBranch;
  supplier: InventorySupplier | null;
  restaurantName: string;
  defaultLocationId: string | null;
  linkExpiresAt: string | null;
  isLive: boolean;
  loading: boolean;
  error: string | null;
}

const mockSession = (): Omit<InventoryAddSession, 'loading' | 'error'> => ({
  sessionToken: null,
  branch: MOCK_BRANCH,
  supplier: MOCK_SUPPLIER,
  restaurantName: MOCK_RESTAURANT_NAME,
  defaultLocationId: null,
  linkExpiresAt: null,
  isLive: false
});

export const useInventoryAddSession = (
  linkId: string | null,
  loggedInToken: string | null
): InventoryAddSession => {
  const [session, setSession] = useState<InventoryAddSession>({
    ...mockSession(),
    loading: true,
    error: null
  });

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        if (linkId) {
          const [context, auth] = await Promise.all([
            getInventoryAddPageContext(linkId),
            verifyInventoryAddLink(linkId)
          ]);

          if (cancelled) return;

          setSession({
            sessionToken: auth.accessToken,
            branch: {
              id: context.branch.id,
              name: context.branch.name,
              address: context.branch.address ?? undefined
            },
            supplier: context.supplier
              ? {
                  id: context.supplier.id,
                  name: context.supplier.business_name || 'Supplier'
                }
              : null,
            restaurantName: context.restaurant.business_name || 'Restaurant',
            defaultLocationId: context.default_location_id,
            linkExpiresAt: context.expires_at,
            isLive: true,
            loading: false,
            error: null
          });
          return;
        }

        if (loggedInToken) {
          if (cancelled) return;
          setSession({
            sessionToken: loggedInToken,
            branch: MOCK_BRANCH,
            supplier: MOCK_SUPPLIER,
            restaurantName: MOCK_RESTAURANT_NAME,
            defaultLocationId: null,
            linkExpiresAt: null,
            isLive: true,
            loading: false,
            error: null
          });
          return;
        }

        if (cancelled) return;
        setSession({
          ...mockSession(),
          loading: false,
          error: null
        });
      } catch (err: unknown) {
        if (cancelled) return;
        const message = axiosIsExpiredLink(err)
          ? 'This inventory link has expired. Ask your supplier for a new link.'
          : err instanceof Error
            ? err.message
            : 'Failed to load inventory page';
        setSession({
          ...mockSession(),
          loading: false,
          error: message
        });
      }
    };

    void init();

    return () => {
      cancelled = true;
    };
  }, [linkId, loggedInToken]);

  return session;
};

const axiosIsExpiredLink = (err: unknown): boolean => {
  if (!err || typeof err !== 'object' || !('response' in err)) return false;
  const response = (
    err as { response?: { data?: { message?: string }; status?: number } }
  ).response;
  const message = response?.data?.message?.toLowerCase() ?? '';
  return response?.status === 401 && message.includes('expired');
};
